// Phase K.3a (2026-08-06) — Shared statement display helpers.
//
// Centralized so list + detail + PDF surfaces render period + identifier
// consistently. Keeps the "friendly month day-day, year" formatter in
// exactly one place.

const MONTH_ABBR = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Format a YYYY-MM-DD (or ISO datetime) date-only string as "Aug 1".
 * Returns "" for null/undefined so callers can no-op fallback.
 */
function _monthDay(dateStr) {
    if (!dateStr) return "";
    const s = String(dateStr).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return dateStr;
    const monthIdx = parseInt(m[2], 10) - 1;
    const day = parseInt(m[3], 10);
    if (monthIdx < 0 || monthIdx > 11) return dateStr;
    return `${MONTH_ABBR[monthIdx]} ${day}`;
}

function _year(dateStr) {
    if (!dateStr) return "";
    const s = String(dateStr).slice(0, 10);
    const m = /^(\d{4})-/.exec(s);
    return m ? m[1] : "";
}

/**
 * Format a period range as "Aug 1 – Aug 31, 2026".
 * Collapses to one year when start + end fall in the same year.
 * Accepts either the K.3a ``period_*_date`` fields or the legacy ISO
 * ``period_*`` strings.
 */
export function formatStatementPeriod(startDate, endDate) {
    if (!startDate && !endDate) return "—";
    if (startDate && !endDate) return `${_monthDay(startDate)}, ${_year(startDate)}`;
    if (!startDate && endDate) return `${_monthDay(endDate)}, ${_year(endDate)}`;
    const yS = _year(startDate);
    const yE = _year(endDate);
    if (yS && yE && yS === yE) {
        return `${_monthDay(startDate)} – ${_monthDay(endDate)}, ${yS}`;
    }
    return `${_monthDay(startDate)}, ${yS} – ${_monthDay(endDate)}, ${yE}`;
}

/**
 * The canonical statement identifier shown to operators + customers.
 * Prefer the backfilled ``statement_number`` (e.g. STMT-2026-08-0007);
 * fall back to a short UUID slice for pre-backfill legacy rows.
 */
export function formatStatementIdentifier(statement) {
    if (!statement) return "—";
    if (statement.statement_number) return statement.statement_number;
    const id = statement.id || "";
    return id ? `${id.slice(0, 8)}…` : "—";
}
